<template>
  <div class="app-theme-editor grid">
    <div class="col-12">
        <breaking-changes-warning v-if="hasBreakingStyles"/>
    </div>
    <slot></slot>
    <div class="app-theme-editor__header col-12">
        <p class="element-badge is--success" v-if="message">{{ message }}</p>
        <p v-else-if="errors.length">
            <span v-for="(error, index) in errors" :key="`error-${index}`" class="element-badge is--error">{{ error }}</span>
        </p>
        <p v-if="isDirty" class="element-badge is--warning">You have unsaved changes.</p>
        <div class="page-main__buttons">
            <button v-if="canSync"
                type="button"
                class="element-button--secondary element-button"
                @click="syncTheme(source)"
                :disabled="isSyncing">
                {{ syncButtonLabel }}
            </button>
            <button 
                type="button"
                class="element-button--primary element-button"
                @click="submit()"
                :disabled="status || disableSave">
                {{ status === 'saving' ? 'Saving...' : 'Save' }}
            </button>
        </div>
    </div>
    <div v-if="!clientLevel" class="app-theme-editor__sections col-12 col-md-6 col-lg-4">
        <div class="app-theme-editor__theme page-main__section">
            <div class="page-main__section-header">
                <h2 class="page-main__section-title">Theme</h2>
                <p class="element-instructions">Select the general design of your project.</p>
            </div>
            <!-- <div v-if="hasLogo">
                <h2>Logo</h2>
                <div class="theme-logo shadow logo--client" v-if="logo">
                    <img :src="logo">
                    <button class="close" @click="logo = null">&times;</button>
                </div>
                <p class="text-muted" v-else>No logo specified.</p>
                <button type="button" class="btn btn-outline-secondary"
                    data-bs-toggle="modal"
                    data-bs-target="#asset-manager-modal"
                    data-bs-backdrop="static">
                    Set Logo
                </button>
                <hr>
            </div> -->

            <div class="input select">
                <label>Theme</label>
                <span v-if="isPhoenix">
                    <select class="form-control" name="theme" id="theme" v-model="theme" v-if="themeDropdownOptions.length">
                        <option v-for="(label, key) in themeDropdownOptions" :key="key" :value="label.id" >
                            {{ label.name }}
                        </option>  
                    </select>
                    <input type="text" class="form-control" name="theme" id="theme" placeholder="i.e. default" v-model="theme" v-else>
                </span>
                <input type="text" class="form-control" name="theme" id="theme" placeholder="i.e. default" v-model="theme" v-else>
            </div>
            <div class="input select" v-if="isPhoenix">
                <label>Color Mode</label>
                <select class="form-control" name="color_mode" v-model="color_mode">
                    <option v-for="(label, key) in color_mode_dropdown_array" :key="key" :value="key" >
                        {{ label }}
                    </option>
                </select> 
            </div>
            <div v-if="hasClientWhitelabelCss" class="app-theme-editor__client-css-toggle">
                <h3 class="app-theme-editor__client-css-toggle-title">Client-level CSS <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                 <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                    <h4 class="app-tooltip__title">Client-level CSS</h4>
                    <div class="app-tooltip__body">
                        <p>If enabled, the project-level CSS here will be used in conjunction with the CSS applied at the client level. If not enabled, the project-level CSS here will be used instead of the client-level CSS.</p>
                    </div>
                </div>
                </h3>
                <label class="element-toggle">
                 <span :class="{ 'is--active': !useClientWhiteLabel }">Off</span>
                    <input
                        id="theme-client-css-toggle"
                        v-model="useClientWhiteLabel"
                        type="checkbox"
                        class="element-toggle__toggle"
                    >
                    <span :class="{ 'is--active': useClientWhiteLabel }">On</span>
                </label>
            </div>
        </div>
        <div v-if="projectGlobalSharedSettingsUrl" class="app-theme-editor__icons page-main__section">
            <div class="page-main__section-header">
                <h2 class="page-main__section-title">Icons and Logo</h2>
                <p class="element-instructions">To replace the icons that are used in this project or to add a logo to the navigation, go to the 
                    <a class="element-link" :href="projectGlobalSharedSettingsUrl">Global Data Shared Settings</a>
                    page.
                </p>
            </div>
        </div>
        <div v-if="canSync" class="app-theme-editor__variables-portal page-main__section">
            <div class="app-theme-editor__variables-portal-header page-main__section-header">
                <h2 class="page-main__section-title">Portal Variables</h2>
                <p class="element-instructions">Define variables inherited from your Portal integration.</p>
            </div>
            <div v-if="portalVariables && portalVariables.length">
                <div v-for="(variable, index) in portalVariables" :key="`themevars-${index}`" class="app-theme-editor__variables-list">
                    <div class="app-theme-editor__variables-name input text">
                        <input type="text" v-model="portalVariables[index].name" placeholder="$variable-name" :disabled="status" readonly>
                    </div>
                    <div class="input text">
                        <input type="text" v-model="portalVariables[index].value" placeholder="#f0f0f0" :disabled="status" readonly>
                    </div>
                </div>
            </div>
            <div v-else class="app-theme-editor__variables-empty">No variables defined. Access your Portal integration to add variables.</div>
        </div>
        <div v-if="!isPhoenix" class="app-theme-editor__variables-local page-main__section">
            <div class="app-theme-editor__variables-local-header page-main__section-header">
                <h2 class="page-main__section-title">Local Variables</h2>
                <p class="element-instructions">Define variables to use specific for your project.</p>
                <button class="element-button--secondary element-button" @click.prevent="addVariable()" :disabled="status">Create New Variable</button>
            </div>
            <div v-for="(variable, index) in variables" :key="`themevars-${index}`" class="app-theme-editor__variables-list">
                <div class="app-theme-editor__variables-name input text">
                    <input type="text" v-model="variables[index].name" placeholder="$variable-name" :disabled="status">
                </div>
                <div class="input text">
                    <input type="text" v-model="variables[index].value" placeholder="#f0f0f0" :disabled="status">
                </div>
                <button class="element-button--icon element-button" @click="variables.splice(index, 1)" :disabled="status">
                    <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                </button>
            </div>
        </div>
    </div>
    <div :class="['app-theme-editor__whitelabel', 'page-main__section', 'col-12', 'col-md-6', 'col-lg-8', {['col-md-12 col-lg-12'] : clientLevel}]" v-if="allowCustom">
        <div class="app-theme-editor__whitelabel-header page-main__section-header">
            <h2 class="page-main__section-title" v-if="isPhoenix">
                <span v-if="clientLevel">Client Phoenix Driver</span>
                White Label (CSS)
            </h2>
            <h2 class="page-main__section-title" v-else>
                <span v-if="clientLevel">Client Driver</span>
                White Label (SCSS)
            </h2>
            <div class="app-theme-editor__revisions dropdown">
                <button
                    type="button"
                    data-bs-toggle="dropdown"
                    @click="updateHistory(currentWhitelabelRevision.project_id)">
                    <span class="element-chip is--info">
                        Rev {{ currentWhitelabelRevision.revision }}
                        <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
                    </span>
                </button>

                <div class="app-dropdown dropdown-menu dropdown-menu-right">
                    <h6 class="app-dropdown__title">
                        Revision History
                    </h6>
                    <div v-show="loadingHistory" class="app-loader__wrapper">
                        <app-loader />
                    </div>
                    <div v-if="!currentHistory.length" class="app-dropdown__body">
                        <em>No history available.</em>
                    </div>
                    <button v-for="(revision, index) in currentHistory"
                        :key="`rev-${index}-${revision.id}`"
                        type="button"
                        :title="localizeDate(revision.updated_at).format('dddd, MMMM Do YYYY, h:mm:ss a')"
                        @click="selectRevision(revision)">
                        <span :class="['element-badge', currentWhitelabelRevision.revision === revision.revision ? 'badge-primary' : 'badge-light']">Rev {{ revision.revision }}</span>
                        <p>
                            <em>{{ localizeDate(revision.updated_at).fromNow() }}</em>
                            <span v-if="revision.editor"> by {{ revision.editor.name }}</span>
                        </p>
                    </button>
                </div>
            </div>
            <p v-if="clientLevel" class="element-instructions">Create a white label that is applied to this client's projects using variable updates in this stylesheet. By default, these styles, along with project-specific styles, will be applied to all projects.</p>
            <p v-else class="element-instructions">Customize your selected theme for your project through variable updates in this stylesheet. These styles will be appended to the stylesheet compilation process.</p>
            <app-button @click="openAssets()" class="element-button--secondary element-button">
                Insert Asset URL
            </app-button>
        </div>
        <div class="page-main__section-body grid">
            <div class="app-theme-editor__version-control-toggle col-12">
                <h3 class="app-theme-editor__version-control-toggle-title">
                    Version Control
                    <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                    <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                    <h4 class="app-tooltip__title">Version Control</h4>
                    <div class="app-tooltip__body">
                        <p>This will create a revision history that you can revert back to each time you select Save. To prevent cluttering the version history, this should be disabled when making frequent, small changes.</p>
                        </div>
                    </div>
                </h3>
                <label class="element-toggle" for="theme-version-control-toggle">
                    <span :class="{ 'is--active': !versionControlEnabled }">Off</span>
                    <input
                        id="theme-version-control-toggle"
                        v-model="versionControlEnabled"
                        type="checkbox"
                        class="element-toggle__toggle"
                        :disabled="status"
                    >
                    <span :class="{ 'is--active': versionControlEnabled }">On</span>
                </label>
                <p v-if="!versionControlEnabled" class="inline-message is--warning">
                    Version control is currently disabled. Any changes made and saved here will not be recoverable.
                </p>
            </div>
            <div v-if="clientLevel && clientNotification" class="app-toast is--inline col-12">
                <div class="app-toast__text">
                    <h3 class="app-toast__title">You must enable Client-level white labeling</h3>
                    <p>Turn on the "Client-level CSS" toggle—located in the Theme section—to enable client-level white labeling on each project.</p>
                </div>
                <button type="button" title="Close Notification" class="app-toast__close element-button--icon element-button" @click="clientNotification = false">
                    <svg aria-hidden="true" class="element-icon--x element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-x"></use></svg>
                </button>
            </div>
            <code-editor ref="style-editor" :value="styles" :language="isPhoenix ? 'css' : 'scss'" class="text-box col-12" :disabled="status" @inputText="validateText($event)" @change="disableSave = false" @validationError="handleValidationError($event)"></code-editor>
        </div>
    </div>
    <div class="col-md-8" v-else>
        <!-- display something else -->
    </div>
  </div>
</template>

<script>
import $ from 'jquery'
import _ from 'lodash'
import { mapState } from 'vuex'
import Echo from '../helpers/echo'
import { MODEL_MAP } from '../helpers/builder'
import axios from 'axios'
import ThemeVariable from './ThemeVariable.vue'
import api from '../api/publisher'
import builderApi from '../api/builder'
import { uuid, localizeDate } from '../helpers/util'
import BreakingChangesWarning from './BreakingChangesWarning.vue'

export default {
    components: { 
        ThemeVariable,
        BreakingChangesWarning
     },
    props: {
        url: {
            type: String,
            required: true
        },
        source: {
            type: Object,
            required: true
        },
        globalSharedDataUrl: {
            type: String,
            default: null
        },
        allowCustom: {
            type: Boolean,
            default: true
        },
        schemaVersions: {
            type: [ Object, Array, null ],
            required: true
        },
        clientLevel: {
            type: Object,
            required: false,
            default: null
        },
        hasClientWhitelabel: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data () {
        return {
            variables: _.map(this.source.theme_vars, (value, name) => {
                return { name, value }
            }),
            portalVariables: _.map(this.source.portal_theme_vars, (value, name) => {
                return { name, value }
            }),
            logo: this.source.logo,
            theme: this.source.theme,
            color_mode: this.source.color_mode ?? '',
            currentWhitelabelRevision: this.source.current_whitelabel_revision ?? { 'revision':'N/A', 'project_id': this.source.id },
            color_mode_dropdown_array: {'':'None', 'light':'Light', 'dark':'Dark'},
            versionControlEnabled: this.source.driver?.category === 'phoenix',
            useClientWhiteLabel: Boolean(this.source.use_client_white_label) ?? false,
            alreadyResaved: false,
            message: null,
            project: this.source.project || this.source,
            errors: [],
            disableSave: false,
            isDifferentRevision: false,
            clientNotification: true,
            status: null,
            isSyncing: false,
            originalRevision: this.source.current_whitelabel_revision ? this.source.current_whitelabel_revision.revision : null,
            revisionHistoryWhitelabel: {},
            loadingHistory: false,
            showVersionControl: false,
            isDirty: false,
            stylesText: ''
        }
    },
    computed: {
        ...mapState('builder', {
            client: state => state.client
        }),
        selectedId () {
            return this.currentWhitelabelRevision?.id || this.source.id
        },
        hasLogo () {
            return this.source.hasOwnProperty('logo')
        },
        canSync () {
            return !!this.client?.integration && !this.clientLevel
        },
        isPhoenix () {
            return this.source.driver?.category === 'phoenix'
        },
        themeDropdownOptions () {
            const dropDownOptions = [
                {
                    id: "default",
                    name: "None (Default)",
                }
            ];
            const currentThemeOptions = this.source.driver?.settings?.themes ?? []
            const result = dropDownOptions.map(option => {
            const found = currentThemeOptions.find(currentOption => currentOption.id === option.id);
                return found ? found : option;
            }).concat(currentThemeOptions.filter(currentOption => !dropDownOptions.some(option => option.id === currentOption.id)));

            return result
        },
        syncButtonLabel () {
            return this.isSyncing ? 'Syncing to Portal...' : 'Sync to Portal'
        },
        hasBreakingStyles () {
            return this.schemaVersions?.styles_version === true && !this.alreadyResaved
        },
        projectGlobalSharedSettingsUrl () {
            if (!this.isPhoenix || !this.globalSharedDataUrl)
                return null

            return this.globalSharedDataUrl
        },
        currentHistory () {
            const projectId = this.currentWhitelabelRevision?.project_id || this.project?.id || this.source.id
            if (!projectId) {
                return []
            }

            return this.revisionHistoryWhitelabel[projectId] || []
        },
        styles () {
            if (this.currentWhitelabelRevision?.styles) {
                return this.currentWhitelabelRevision.styles
            } else {
                return this.project?.styles || this.source.styles || ''
            }
        },
        hasClientWhitelabelCss () {
            return !this.clientLevel && this.hasClientWhitelabel && this.source.driver?.category === 'phoenix'
        }
    },
    watch: {
        revisionHistoryWhitelabel (value, oldVal) {
            // i dont think this is doing anything
            console.log('revision history updated', value)
            if (value !== oldVal) {
                console.log('history updated, setting loading to false')
                this.loading = true 
            }
        }
    },
    methods: {
        localizeDate: localizeDate,
        addVariable () {
            this.variables.push({
                name: '',
                value: ''
            })
        },
        openAssets () {
            const $manager = $('#asset-manager-modal')
            $manager.modal('show')
        },
        handleValidationError (error) {
            if (error !== null) {
                this.disableSave = true
            } else {
                this.disableSave = false
            }
        },
        async submit () {
            const valid = await this.$refs['style-editor'].onInput(this.$refs['style-editor'].getValue(), true) // trigger validation
            console.log('validation result', valid)
            if (!valid) {
                return
            }
            this.status = 'saving'

            // generate formdata
            const formData = new FormData()

            formData.append('_method', 'PUT')
            formData.append('theme', this.theme)
            formData.append('styles', this.$refs['style-editor'].getValue())
            if (this.hasLogo) {
                formData.append('logo', this.logo)
            }
            if(this.useClientWhiteLabel && this.hasClientWhitelabelCss) {
                formData.append('use_client_white_label', 1)
            } else if (!this.useClientWhiteLabel && this.hasClientWhitelabelCss) {
                formData.append('use_client_white_label', 0)
            }
            if (this.versionControlEnabled) {
                formData.append('version_control_enabled', 1)
            } else {
                formData.append('version_control_enabled', 0)
            }
            if(this.isDifferentRevision) {
                formData.append('style_revision_id', this.currentWhitelabelRevision.id)
            }

            // attach all theme vars
            const variables = _.filter(this.variables, ({ name, value }) => name.length && value.length)
            if (variables.length) {
                _.each(variables, ({ name, value }) => {
                    if (name && value) {
                        formData.append(`theme_vars[${name}]`, value)
                    }
                })
            } else {
                formData.append('theme_vars', [])
            }

            formData.append('color_mode', this.color_mode)

            try {
                this.message = null
                const response = await builderApi.update(this.url, formData)

                this.errors = _.get(response.data, 'errors', [])
                if (!this.errors.length) {
                    this.message = 'Design successfully updated.'
                    this.alreadyResaved = true
                }
                this.isDirty = false
                if (this.versionControlEnabled) {
                    await this.updateHistory(this.project.id)
                    const history = this.revisionHistoryWhitelabel[this.project.id] || []
                    let revision = history.find(revision => revision.id === response.style_revision_id)
                    if (revision)
                    {
                        this.currentWhitelabelRevision = {...revision, styles: response.styles}      
                    }
                }
            } catch (err) {
                console.error(err)
                this.errors = _.get(err, 'response.data.errors', [])
            }

            this.status = null
        },
        async setupAssetManager () {
            const $manager = $('#asset-manager-modal')

            this.$store.commit('assets/setOptions', { assetType: ['image', 'font'] })

            if (!this.$store.state.assets.loaded) {
                await this.$store.dispatch('assets/update')
            }

            $manager.on('show.bs.modal', () => {
                $manager.one('selected', (event, { file }) => {
                    // insert text
                    const value = `"/assets/${file.path}"`
                    this.$refs['style-editor'].insert(value)
                })
            })

            $manager.on('hide.bs.modal', () => {
                $manager.off('selected')
            })
        },
        async syncTheme(project) {
            this.isSyncing = true
            await api.syncTheme(project.id)
        },
        processNotification (notification) {
            console.log(notification)
            if (notification.progress === 1) {
                this.isSyncing = false
                this.driverSyncedAt = notification.model.driver_synced_at
            }
        },
        async updateHistory (id) {
            this.loadingHistory = true
             try {
                await this.getRevisionHistoryWhitelabel(id)
            } finally {
                this.loadingHistory = false
            }
        },
        async selectRevision (revision) {

            if (!confirm('Are you sure you want to replace your local changes with the selected revision?')) {
                return
            }
            const result = {
                id: revision.project_id,
                styles: revision.styles,
                revision: revision.revision
            }
            if (this.currentWhitelabelRevision.revision !== revision.revision) {
                this.isDirty = true
            }
            
            if (!result.styles) {
                this.loadingHistory = true
                const revisionData = await this.getRevisionDataWhitelabel(revision)
                result.styles = revisionData.styles
                this.loadingHistory = false
            }
            this.currentWhitelabelRevision = { revision: result.revision, project_id: result.id, styles: result.styles, id: revision.id }
            if (this.currentWhitelabelRevision.revision !== this.source.current_whitelabel_revision?.revision) {
                this.isDifferentRevision = true
            } else {
                this.isDifferentRevision = false
            }
            this.project.styles = result.styles
            this.refreshTheme();
        },
        validateText (value) {
            const normalizeText = (text) => (text || '').replace(/\r\n/g, '\n').trimEnd()
            const editorVal = normalizeText(typeof value === 'string' ? value : value.target.value)
            const storedVal = normalizeText(this.currentWhitelabelRevision.styles)
            this.isDirty = editorVal !== storedVal
            if (this.isDirty) this.isDifferentRevision = false
        },
        async refreshTheme () {
            const projectId = this.project?.id || this.source.id
            const project = await builderApi.refreshTheme(projectId)
            this.project = project
        },
        async getRevisionHistoryWhitelabel (projectId) {
            const revisions = await builderApi.getRevisionsWhitelabel(projectId)
            if (revisions) {
                this.revisionHistoryWhitelabel = { ...this.revisionHistoryWhitelabel, [projectId]: revisions }
            }
        },
        async getRevisionDataWhitelabel (source) {
            const revision = await builderApi.getRevisionWhitelabel(source.project_id, source.id)
            return revision
        },
    },
    mounted () {
        this.setupAssetManager()
    },
    beforeDestroy () {
        Echo.leave(`${MODEL_MAP['project'].model}.${this.source.id}`)
    },
    created () {
        this.validateText = _.debounce(this.validateText, 300)

        this.$store.dispatch('builder/initialize', {
            source: this.source,
            type: 'project'
        })

        Echo.private(`${MODEL_MAP['project'].model}.${this.source.id}`)
            .notification((notification) => {
                this.processNotification(notification)
            })
    }
}
</script>
